import React, { useState, useEffect, useMemo } from "react";
import { Paper, IconButton, Button, Typography, Box, LinearProgress } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import DataQualityMain from "./DataQualityMain";
import axios from "axios";
import { Check, Save, Upload, Download } from "lucide-react";
import { toast, Toaster } from "react-hot-toast";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import Plot from "react-plotly.js";
import heartLoading from "../../../assests/heartLoading.gif";
import Tooltip from "@mui/material/Tooltip";
import DQSummaryTable from "./components/DQSummaryTable";
import DQResultsTable from "./components/DQResultsTable";
import DQRuleSuggestionsTable from "./components/DQRuleSuggestionsTable";
import UploadRuleExcelDialog from "./components/UploadRuleExcelDialog";
import { downloadTemplate } from "../../../utils/downloadTemplate";
import ErrorSchemaSection from "./components/ErrorSchemaSection";

export default function DataQuality({ onPrevious, nextStep, id, userRole }) {
  const [openModal, setOpenModal] = useState(false);
  const [openResultModal, setOpenResultModal] = useState(false);
  const [openRuleSuggestionsModal, setOpenRuleSuggestionsModal] =
    useState(false);
  const [assetId, setAssetId] = useState("");
  const [assetName, setAssetName] = useState("");
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataQualityResults, setDataQualityResults] = useState();
  const [error, setError] = useState("");
  const [finalResult, setFinalResult] = useState(false);
  const [dqResultAsset, setDQResultAsset] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [completedRecords, setCompletedRecords] = useState(0);
  const [qualityScore, setQualityScore] = useState(0);
  const [weightage, setWeightage] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [ruleSuggestions, setRuleSuggestions] = useState([]);
  const [ruleSuggestionsLoading, setRuleSuggestionsLoading] = useState(false);
  const [ruleSuggestionsLoadingId, setRuleSuggestionsLoadingId] = useState(null);
  const [dqJobStatusLoading, setDqJobStatusLoading] = useState(false);
  const [runDqLoading, setRunDqLoading] = useState(false);
  const [runDqLoadingAssetId, setRunDqLoadingAssetId] = useState(null);
  const [hasResultsFetched, setHasResultsFetched] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isUploadHovered, setIsUploadHovered] = useState(false);
  const [isDownloadHovered, setIsDownloadHovered] = useState(false);

  const getQualityScoreColor = (score) => {
    if (score > 85) return "#4CAF50";
    if (score >= 55) return "#FF9800";
    if (score < 55) return "#F44336";
    return "#000";
  };

  useEffect(() => {
    setLoading(true);
    const BASE_URL = process.env.REACT_APP_API_URL;
    axios
      .get(`${BASE_URL}/asset-list/${id}`)
      .then((res) => {
        const fetchedAssets = res.data;
        setAssets(fetchedAssets);
        pollStatus(fetchedAssets);
      })
      .catch((err) => {
        setAssets([]);
        setLoading(false);
      });
  }, [id]);

  const fetchDQJobResults = () => {
    if (hasResultsFetched) return;

    try {
      axios
        .get(`${process.env.REACT_APP_API_URL}/runQuality/ruleStatus/${id}`)
        .then((response) => {
          if (response?.status === 200 || response?.status === 201) {
            setDataQualityResults(response?.data);
            setFinalResult(true);
            setHasResultsFetched(true);
            setLoading(false);
          }
        });
    } catch (err) {
      setError("Failed to load profiling results.");
      setHasResultsFetched(true);
      setLoading(false);
    }
  };

  const pollStatus = (assets) => {
    try {
      axios
        .get(`${process.env.REACT_APP_API_URL}/runQuality/status/${id}`)
        // Update job status
        .then((response) => {
          if (
            response?.data?.overall_status == "Success" ||
            response?.data?.overall_status == "Failed"
          ) {
            const assetList = assets?.map((as) => {
              const matchedAsset = response?.data?.dq_jobs?.find(
                (job) => job.data_asset_name === as.name
              );
              if (matchedAsset)
                return { ...as, dq_job_status: matchedAsset.status };
              return as;
            });
            setAssets(assetList);
            setDqJobStatusLoading(false);
            fetchDQJobResults();
          } else if (response?.data?.overall_status === "In Progress") {
            setLoading(false);
            setDqJobStatusLoading(true);
            // Continue polling every 60 seconds
            setTimeout(() => pollStatus(assets), 60000);
          } else {
            setLoading(false);
            setDqJobStatusLoading(false);
          }
        });
    } catch (err) {
      setError("Failed to poll dq rule job status.");
      setLoading(false);
      setDqJobStatusLoading(false);
    }
  };

  const handleRunDQAsset = async (assetId) => {
    try {
      setRunDqLoadingAssetId(assetId);
      const BASE_URL = process.env.REACT_APP_API_URL;
      await axios.post(`${BASE_URL}/runQuality/asset/${assetId}`);
      toast.success("DQ check started for this asset!");
      setHasResultsFetched(false);
      setTimeout(() => {
        setDqJobStatusLoading(true);
        pollStatus(assets);
      }, 2000);
    } catch (err) {
      toast.error("Failed to run DQ check. Please try again.");
    } finally {
      setRunDqLoadingAssetId(null);
    }
  };

  const handleRunDQ = async () => {
    try {
      setRunDqLoading(true);
      const BASE_URL = process.env.REACT_APP_API_URL;
      await axios.post(`${BASE_URL}/runQuality/product/${id}`);
      toast.success("DQ checks running successfully!");
      setHasResultsFetched(false);
      setTimeout(() => {
        setDqJobStatusLoading(true);
        // Trigger the polling flow by calling pollStatus
        pollStatus(assets);
      }, 2000);
    } catch (err) {
      toast.error("Failed to run DQ checks. Please try again.");
    } finally {
      setRunDqLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    downloadTemplate();
  };

  const handleViewResult = async (assetId) => {
    const resultByAsset =
      dataQualityResults.find((res) => res.data_asset_id === assetId) || null;
    const filteredRules =
      resultByAsset?.rules?.filter((rule) => rule.rule_name !== "row_count") ||
      null;
    const total = resultByAsset?.rules?.length || 0;
    const completed =
      resultByAsset?.rules?.filter((rule) => rule.dq_result !== "pass")
        ?.length || 0;
    const score = resultByAsset?.latest_dimension_scores.score || 0;
    const dimensionWeightage = resultByAsset?.latest_dimension_scores || null;
    const chart = resultByAsset?.score_trend_plot_config || null;
    const updatedWeightage = { ...dimensionWeightage };
    delete updatedWeightage.score;

    setDQResultAsset({ ...resultByAsset, rules: filteredRules });
    setTotalRecords(total);
    setCompletedRecords(completed);
    setQualityScore(score);
    setWeightage(updatedWeightage);
    setChartData(chart);
  };

  const fetchRuleSuggestions = async (assetId) => {
    try {
      setRuleSuggestionsLoadingId(assetId);
      setRuleSuggestionsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/rule-suggestions/${assetId}`
      );
      if (response?.status === 200) {
        setRuleSuggestions(response.data);
        setOpenRuleSuggestionsModal(true);
      }
    } catch (err) {
      console.error("Error fetching rule suggestions:", err);
      toast.error("Failed to fetch rule suggestions. Please try again.");
    } finally {
      setRuleSuggestionsLoading(false);
      setRuleSuggestionsLoadingId(null);
        setRuleSuggestionsLoading(false);
        setRuleSuggestionsLoadingId(null);
    }
  };

  const renderDQStatus = (status) => {
    if (!status)
      return (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 2,
            py: 0.5,
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: 600,
            textTransform: "uppercase",
            backgroundColor: "#fff3e0",
            color: "#f57c00",
          }}
        >
          Pending
        </Box>
      );
    const background =
      status === "Completed"
        ? "#e8f5e8"
        : status === "Failed"
        ? "#ffeaea"
        : "#fff3e0";
    const color =
      status === "Completed"
        ? "#2e7d32"
        : status === "Failed"
        ? "#d32f2f"
        : "#f57c00";
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 2,
          py: 0.5,
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: 600,
          textTransform: "uppercase",
          backgroundColor: background,
          color,
        }}
      >
        {status}
      </Box>
    );
  };

  const dqScoreTrendLayout = useMemo(() => {
    const baseLayout = { ...(chartData?.layout || {}) };
    if (baseLayout) {
      delete baseLayout.height;
      delete baseLayout.width;
    }
    return {
      ...baseLayout,
      margin: { t: 40, b: 60, l: 60, r: 40, ...(chartData?.layout?.margin || {}) },
      autosize: true,
    };
  }, [chartData]);

  const dqScoreTrendConfig = useMemo(
    () => ({ displayModeBar: false, responsive: true }),
    []
  );

  console.log("loading  vlaue", loading);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." className="h-30" />
          <span className="text-gray-700 text-[20px]">
            Hang tight! The resources are loading...
          </span>
        </div>
      </div>
    );
  }

  if (dqJobStatusLoading) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 700,
          margin: "auto",
          mt: 16,
          border: "1px solid #dacdcd",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <img
            src={heartLoading}
            alt="loading"
            className="h-[100px] block m-auto animate-pulse"
          />
          <Typography variant="h5" component="h2" sx={{ mt: 2, mb: 1 }}>
            DQ check in Progress
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Please wait while we analyze the data quality rules. This might take
            a few moments.
          </Typography>
          <LinearProgress />
        </Box>
      </Paper>
    );
  }
  return (
    <>
      <div className="w-[90%] m-auto mt-6">
        {/* Action Buttons - Top Right */}
        <div className="flex justify-end space-x-4 mb-4">
          <button
            onClick={handleDownloadTemplate}
            onMouseEnter={() => setIsDownloadHovered(true)}
            onMouseLeave={() => setIsDownloadHovered(false)}
            className={`flex items-center space-x-2 px-4 h-[45px] rounded-lg transition-colors duration-200 shadow-md border border-[#bb1ccc] ${
              isDownloadHovered ? "bg-[#bb1ccc] text-white" : "bg-white text-[#bb1ccc]"
            }`}
          >
            <Download size={18} />
            <span>Download Template</span>
          </button>
          <button
            onClick={() => setUploadDialogOpen(true)}
            onMouseEnter={() => setIsUploadHovered(true)}
            onMouseLeave={() => setIsUploadHovered(false)}
            className={`flex items-center space-x-2 px-4 h-[45px] rounded-lg transition-colors duration-200 shadow-md border border-[#bb1ccc] ${
              isUploadHovered ? "bg-[#bb1ccc] text-white" : "bg-white text-[#bb1ccc]"
            }`}
          >
            <Upload size={18} />
            <span>Upload Excel</span>
          </button>
          <button
            onClick={handleRunDQ}
            disabled={runDqLoading}
            className={`flex items-center space-x-2 px-4 h-[45px] rounded-lg transition-colors duration-200 shadow-md ${
              runDqLoading
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-[#bb1ccc] text-white hover:bg-[#000080]"
            }`}
          >
            <Save size={18} />
            <span>{runDqLoading ? "Loading..." : "Run DQ Checks"}</span>
          </button>
          {(() => {
            const dataEngineerVendorRoles = ["Data Engineer", "Vendor"];
            const isDisabled =
              dataEngineerVendorRoles.includes(userRole) || !dataQualityResults;
            const disabledReason = dataEngineerVendorRoles.includes(userRole)
              ? "You are not allowed to go to publish page"
              : "DQ check has not run yet";

            return (
              <Tooltip
                title={isDisabled ? disabledReason : ""}
                placement="top"
                arrow
              >
                <span>
                  <button
                    onClick={() => nextStep()}
                    disabled={isDisabled}
                    className={`flex items-center space-x-2 px-4 h-[45px] rounded-lg transition-colors duration-200 shadow-md ${
                      isDisabled
                        ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                        : "bg-[#bb1ccc] text-white hover:bg-[#000080]"
                    }`}
                  >
                    <Check size={18} />
                    <span>Ready to Publish?</span>
                  </button>
                </span>
              </Tooltip>
            );
          })()}
        </div>

        <DQSummaryTable
          assets={assets}
          finalResult={finalResult}
          ruleSuggestionsLoading={ruleSuggestionsLoading}
          ruleSuggestionsLoadingId={ruleSuggestionsLoadingId}
          onFetchRuleSuggestions={fetchRuleSuggestions}
          onOpenConfig={(assetId, assetName) => {
            setAssetId(assetId);
            setAssetName(assetName);
            setOpenModal(true);
          }}
          onViewResult={(assetId) => {
            handleViewResult(assetId);
            setOpenResultModal(true);
          }}
          getDQStatusPill={renderDQStatus}
          onRunDQAsset={handleRunDQAsset}
          runDqLoadingAssetId={runDqLoadingAssetId}
        />

        {openResultModal && dqResultAsset ? (
          <Dialog
            open={openResultModal}
            onClose={() => setOpenResultModal(false)}
            maxWidth="xl"
            fullWidth
          >
            <div className="flex justify-between items-center ">
              <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>
                DQ Results
              </DialogTitle>
              <IconButton
                sx={{ marginRight: "20px" }}
                onClick={() => setOpenResultModal(false)}
              >
                <CloseIcon />
              </IconButton>
            </div>
            <DialogContent>
              <div>
                {/* Disclaimer Section */}
                <Box
                  sx={{
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffeaa7",
                    borderRadius: "12px",
                    padding: "16px 20px",
                    marginBottom: "24px",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Box
                    sx={{
                      color: "#856404",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "#fff3cd",
                      border: "2px solid #856404",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      ⚠
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#856404",
                        marginBottom: "4px",
                        lineHeight: 1.4,
                      }}
                    >
                      Disclaimer
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#856404",
                        lineHeight: 1.5,
                        opacity: 0.9,
                      }}
                    >
                      If there are no other rules apart from row count and
                      schema check, results will not be available. Please add
                      more rules to see complete results.
                    </Typography>
                  </Box>
                </Box>

                {/* Stats Cards Row */}
                <Box sx={{ display: "flex", gap: 4, mb: 3, alignItems: "stretch" }}>
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "16px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                      padding: 3,
                      width: "200px",
                      height: "120px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      border: "1px solid #d3d3d3",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: "0 12px 30px rgba(0, 0, 0, 0.2) !important",
                        transform: "translateY(-6px) !important",
                        border: "1px solid #bb1ccc !important",
                        backgroundColor: "#f8f9ff",
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "#bb1ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        DQ Score
                      </Typography>
                      <Box
                        sx={{
                          height: "3px",
                          width: "40px",
                          backgroundColor: "#bb1ccc",
                          borderRadius: "2px",
                          mt: 0.5,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color: getQualityScoreColor(qualityScore),
                        fontWeight: 700,
                        fontSize: "2rem",
                      }}
                    >
                      {qualityScore}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "16px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                      padding: 3,
                      width: "200px",
                      height: "120px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      border: "1px solid #d3d3d3",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: "0 12px 30px rgba(0, 0, 0, 0.2) !important",
                        transform: "translateY(-6px) !important",
                        border: "1px solid #bb1ccc !important",
                        backgroundColor: "#f8f9ff",
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "#bb1ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        Rules Processed
                      </Typography>
                      <Box
                        sx={{
                          height: "3px",
                          width: "40px",
                          backgroundColor: "#bb1ccc",
                          borderRadius: "2px",
                          mt: 0.5,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#3C3C3C",
                        fontSize: "2rem",
                      }}
                    >
                      {totalRecords}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "16px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                      padding: 3,
                      width: "200px",
                      height: "120px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      border: "1px solid #d3d3d3",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: "0 12px 30px rgba(0, 0, 0, 0.2) !important",
                        transform: "translateY(-6px) !important",
                        border: "1px solid #bb1ccc !important",
                        backgroundColor: "#f8f9ff",
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "#bb1ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        Rules Failed
                      </Typography>
                      <Box
                        sx={{
                          height: "3px",
                          width: "40px",
                          backgroundColor: "#bb1ccc",
                          borderRadius: "2px",
                          mt: 0.5,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#3C3C3C",
                        fontSize: "2rem",
                      }}
                    >
                      {completedRecords}
                    </Typography>
                  </Box>
                </Box>

                {/* Two Column Layout */}
                <Box sx={{ display: "flex", gap: 4, mb: 3 }}>
                  {/* Left Column - Weightage */}
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "16px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                      padding: 3,
                      border: "1px solid #d3d3d3",
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#bb1ccc",
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        marginBottom: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: "4px",
                          height: "20px",
                          backgroundColor: "#bb1ccc",
                          borderRadius: "2px",
                        }}
                      />
                      Dimension Level Score
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: 3,
                        maxWidth: "100%",
                      }}
                    >
                      {weightage &&
                        Object.entries(weightage)
                          .filter(([key]) => key.endsWith("_weightage")) // Only process weightage entries
                          .map(([key, value]) => {
                            // Extract score and weightage from the API response structure
                            const dimensionName = key.replace("_weightage", "");
                            const score =
                              dqResultAsset?.latest_dimension_scores?.[
                                dimensionName
                              ] || 0;
                            const weightageValue = value;
                            const contribution =
                              score > 0 && weightageValue > 0
                                ? ((score * weightageValue) / 100).toFixed(2)
                                : 0;

                            return (
                              <Box
                                key={key}
                                sx={{
                                  backgroundColor:
                                    weightageValue > 0 ? "#f0f9ff" : "#e5e2e2",
                                  borderRadius: "12px",
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                  padding: 3,
                                  border: "1px solid",
                                  borderColor:
                                    weightageValue > 0 ? "#e0f2fe" : "#e5e2e2",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
                                    transform: "translateY(-2px)",
                                  },
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                    color: "#1f2937",
                                    marginBottom: 2,
                                    textTransform: "uppercase",
                                    textAlign: "center",
                                  }}
                                >
                                  {dimensionName.charAt(0).toUpperCase() +
                                    dimensionName.slice(1)}
                                </Typography>

                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                  }}
                                >
                                  {/* Score */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.875rem",
                                        color: "#6b7280",
                                      }}
                                    >
                                      Score
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: "1.1rem",
                                        color:
                                          score > 0 ? "#059669" : "#dc2626",
                                      }}
                                    >
                                      {score}
                                    </Typography>
                                  </Box>

                                  {/* Weightage */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.875rem",
                                        color: "#6b7280",
                                      }}
                                    >
                                      Weightage
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: "1.1rem",
                                        color: "#374151",
                                      }}
                                    >
                                      {weightageValue}%
                                    </Typography>
                                  </Box>

                                  {/* Contribution */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.875rem",
                                        color: "#6b7280",
                                      }}
                                    >
                                      Contribution
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: "1.1rem",
                                        color:
                                          score > 0 ? "#059669" : "#dc2626",
                                      }}
                                    >
                                      {contribution}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                    </Box>
                  </Box>

                  {/* Right Column - Chart */}
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "16px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                      padding: 3,
                      border: "1px solid #d3d3d3",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#bb1ccc",
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        marginBottom: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: "4px",
                          height: "20px",
                          backgroundColor: "#bb1ccc",
                          borderRadius: "2px",
                        }}
                      />
                      DQ Score Trend
                    </Typography>
                    <Box sx={{ width: "100%", flexGrow: 1, display: "flex", alignItems: "stretch" }}>
                      <Plot
                        data={chartData?.data || []}
                        layout={dqScoreTrendLayout}
                        style={{ width: "100%", height: "100%" }}
                        useResizeHandler={true}
                        config={dqScoreTrendConfig}
                      />
                    </Box>
                  </Box>
                </Box>

                <DQResultsTable
                  rules={dqResultAsset?.rules || []}
                  getQualityScoreColor={getQualityScoreColor}
                />
              </div>
            </DialogContent>
          </Dialog>
        ) : null}

        {/* Note Section */}
        <Box
          sx={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "12px",
            padding: "16px 20px",
            marginTop: "24px",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box
            sx={{
              color: "#856404",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "24px",
              height: "24px",
              borderRadius: "50%",
              backgroundColor: "#fff3cd",
              border: "2px solid #856404",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              ⚠
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#856404",
                marginBottom: "4px",
                lineHeight: 1.4,
              }}
            >
              Note
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: "#856404",
                lineHeight: 1.5,
                opacity: 0.9,
              }}
            >
              To get Error DataFrames from the asset ingestion pipelines,
              configure the pipeline to generate an output DataFrame and name it
              using the format - <strong>{"{asset_name}_output_df"}</strong>
            </Typography>
          </Box>
        </Box>

        <ErrorSchemaSection productId={id} />
      </div>

      {openModal && (
        <DataQualityMain
          setOpenModal={setOpenModal}
          assetId={assetId}
          assetName={assetName}
          productId={id}
        />
      )}

      <UploadRuleExcelDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        productId={id}
        onUploadSuccess={() => setHasResultsFetched(false)}
      />

      {/* Rule Suggestions Modal */}
      {openRuleSuggestionsModal && (
        <Dialog
          open={openRuleSuggestionsModal}
          onClose={() => setOpenRuleSuggestionsModal(false)}
          maxWidth="lg"
          fullWidth
        >
          <div className="flex justify-between items-center">
            <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>
              Recommended Rules for {assetName}
            </DialogTitle>
            <IconButton
              sx={{ marginRight: "20px" }}
              onClick={() => setOpenRuleSuggestionsModal(false)}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <DialogContent>
            <DQRuleSuggestionsTable
              suggestions={ruleSuggestions}
              loading={ruleSuggestionsLoading}
            />
          </DialogContent>
        </Dialog>
      )}

      <Toaster position="top-right" />
    </>
  );
}
